/**
 * useTranslatedCaptionLines — PLAT-9626.
 *
 * Resolves the karaoke lines that drive the captions render path. Captions are a
 * PRODUCED artifact: the hook only surfaces translated captions that were baked
 * into the karaoke track at produce time.
 *
 *   Tier 1 — Every line was produced for a language (`translatedText` set on all
 *            lines): return them with each phrase's lead identity attached (so
 *            the Fix-Translation gesture can stamp the caption word spans).
 *   Tier 3 — Otherwise (no language, or production incomplete): empty array. The
 *            caller falls back to the original-language `KaraokeLinesView`.
 *
 * The on-the-fly "Tier 2" derivation (build caption text live from the cached
 * translation) was removed: a translation edit reaches captions via a scoped
 * karaoke RE-PRODUCE, not live derivation. This keeps a single source of truth
 * (the produced track) and drops the last integer-keyed store read.
 */

import { useMemo } from 'react';

import { isWhitespaceElement } from '../utils';

import type { TranscriptToken } from '@src/editor/types';
import type { KaraokeLine } from '@src/editor/types/karaoke';

/**
 * A derived caption phrase entry. Extends the persisted `KaraokeLine`
 * `translatedPhrases` shape with the lead token's stable identity (PLAT-10457
 * A2): `leadUuid` (`source_uuid ?? id` of the phrase's first covered token in
 * this line) and its `initialIndex`. ADDITIVE + in-memory only — never
 * persisted; A3 stamps these on the caption word spans (`data-source-uuid` /
 * `data-initial-index`) so the Fix-Translation gesture resolves the phrase by
 * stable id first, integer fallback (mirrors prose `TranslatedSortedPhrase`).
 */
export interface DerivedCaptionPhrase {
  text: string;
  ts: number;
  endTs: number;
  originalIndices?: number[];
  leadUuid?: string;
  initialIndex?: number;
}

export function useTranslatedCaptionLines(
  lines: readonly KaraokeLine[],
  language: string | null | undefined,
): KaraokeLine[] {
  return useMemo(() => {
    // No translation language active, or no lines → no translated captions.
    if (!language || !lines || lines.length === 0) return [];
    // Tier 1: every line was produced for the language. Partial production
    // (some lines missing `translatedText`) falls through to Tier 3 rather than
    // rendering a mixed subset — the track is re-produced as a whole.
    // `!= null`, not truthiness: a row the reviewer emptied ("Remove
    // Translation" over all of it) is produced and stores `''`, and reading
    // that as unproduced dropped the whole track to the source language.
    if (!lines.every(line => line.translatedText != null)) return [];
    return lines.map(attachLeadIdentityFromTokens);
  }, [lines, language]);
}

/**
 * PLAT-10457 (A7) — attach the additive lead identity to a PRODUCED (Tier-1)
 * line's `translatedPhrases`, computed from the line's OWN `transcriptTokens`.
 *
 * The persisted `translatedPhrases` carry `originalIndices` but none of the
 * `leadUuid`/`initialIndex` identity the Fix-Translation gesture needs. This
 * helper recovers it tier-independently: it maps the line's NON-whitespace
 * tokens by `initial_index` (whitespace is a separator, not phrase content),
 * then for each phrase takes its first `originalIndices` entry that a token
 * covers as the lead — `initialIndex` = that index, `leadUuid` = the lead
 * token's `source_uuid ?? id`. If no covered token is present the fields stay
 * undefined. ADDITIVE + in-memory only (does NOT widen `karaoke.ts`); every
 * existing phrase field is preserved. Exposed for unit testing.
 */
export function attachLeadIdentityFromTokens(line: KaraokeLine): KaraokeLine {
  const phrases = line.translatedPhrases;
  if (!phrases || phrases.length === 0) return line;

  const tokenByIndex = new Map<number, TranscriptToken>();
  for (const token of line.transcriptTokens) {
    if (isWhitespaceElement(token)) continue;
    tokenByIndex.set(token.initial_index, token);
  }

  const withIdentity: DerivedCaptionPhrase[] = phrases.map(phrase => {
    const leadIdx = phrase.originalIndices?.find(i => tokenByIndex.has(i));
    if (leadIdx === undefined) return phrase;
    const leadToken = tokenByIndex.get(leadIdx);
    if (!leadToken) return phrase;
    return {
      ...phrase,
      initialIndex: leadIdx,
      leadUuid: leadToken.source_uuid ?? leadToken.id,
    };
  });

  return { ...line, translatedPhrases: withIdentity };
}
